import { type StaffCatalogListQuery, staffCatalogListQuerySchema } from "@lafayette/schemas";

// The list filters live in the page address, so a filtered list can be bookmarked, shared and restored
// with the Back button. This module maps the address to the R1 query and back. The R1 schema decides
// what a valid value is: a value in the address that the schema refuses is dropped and reported, and
// never sent.

/** The page size of the list. The API allows up to 100; 25 keeps the first paint fast. */
export const CATALOG_PAGE_SIZE = 25;

export type CatalogFilterQuery = Omit<StaffCatalogListQuery, "cursor" | "limit">;

/** The filter form: every control holds a string (or a list of strings, or a flag). */
export type CatalogFilterValues = Readonly<{
  q: string;
  type: string;
  status: readonly string[];
  countryCode: string;
  locality: string;
  verification: string;
  pricingModel: string;
  fulfilmentMode: string;
  sourceKind: string;
  sourceSystem: string;
  featured: boolean;
  sort: string;
}>;

export const emptyFilterValues: CatalogFilterValues = {
  q: "",
  type: "",
  status: [],
  countryCode: "",
  locality: "",
  verification: "",
  pricingModel: "",
  fulfilmentMode: "",
  sourceKind: "",
  sourceSystem: "",
  featured: false,
  sort: "",
};

const TEXT_KEYS = [
  "q",
  "type",
  "countryCode",
  "locality",
  "verification",
  "pricingModel",
  "fulfilmentMode",
  "sourceKind",
  "sourceSystem",
  "sort",
] as const;

const filterShape = staffCatalogListQuerySchema.shape;

export type ParsedFilters = Readonly<{
  query: CatalogFilterQuery;
  /** The names of address parameters that were dropped (unknown or invalid). */
  ignored: readonly string[];
}>;

/** Reads the filters from the page address. Never throws: an invalid parameter is dropped and named. */
export function searchParamsToQuery(params: URLSearchParams): ParsedFilters {
  const query: Record<string, unknown> = {};
  const ignored: string[] = [];
  const known = new Set<string>([...TEXT_KEYS, "status", "featured"]);

  for (const key of new Set(params.keys())) {
    if (!known.has(key)) {
      ignored.push(key);
      continue;
    }
    const raw = key === "status" ? params.getAll(key) : (params.get(key) ?? "");
    const value = key === "status" ? raw : (raw as string).trim();
    if (value === "" || (Array.isArray(value) && value.length === 0)) continue;
    const shape = filterShape[key as keyof typeof filterShape];
    const parsed = shape.safeParse(value);
    if (parsed.success) {
      if (parsed.data !== undefined) query[key] = parsed.data;
    } else {
      ignored.push(key);
    }
  }
  return { query: query as CatalogFilterQuery, ignored };
}

/** The filter form values for a query (the form shows what the address says). */
export function queryToFilterValues(query: CatalogFilterQuery): CatalogFilterValues {
  return {
    q: query.q ?? "",
    type: query.type ?? "",
    status: query.status ?? [],
    countryCode: query.countryCode ?? "",
    locality: query.locality ?? "",
    verification: query.verification ?? "",
    pricingModel: query.pricingModel ?? "",
    fulfilmentMode: query.fulfilmentMode ?? "",
    sourceKind: query.sourceKind ?? "",
    sourceSystem: query.sourceSystem ?? "",
    featured: query.featured === true,
    sort: query.sort ?? "",
  };
}

/** The page address for the form values. An empty control leaves its parameter out. */
export function filterValuesToSearchParams(values: CatalogFilterValues): URLSearchParams {
  const params = new URLSearchParams();
  for (const key of TEXT_KEYS) {
    const raw = values[key].trim();
    if (raw === "") continue;
    params.set(key, key === "countryCode" ? raw.toUpperCase() : raw);
  }
  for (const status of values.status) params.append("status", status);
  if (values.featured) params.set("featured", "true");
  return params;
}

/** The R1 query for one page. `cursor` is the opaque value of the previous page's `nextCursor`. */
export function listQueryFor(
  filters: CatalogFilterQuery,
  cursor?: string | null,
  limit: number = CATALOG_PAGE_SIZE,
): StaffCatalogListQuery {
  return {
    ...filters,
    limit,
    ...(cursor === undefined || cursor === null ? {} : { cursor }),
  };
}

/** How many filters narrow the list (the sort order does not). */
export function activeFilterCount(query: CatalogFilterQuery): number {
  return Object.keys(query).filter((key) => key !== "sort").length;
}
